<template>
  <div class="container">
    <form
      :id="id"
      ref="form"
      :key="key"
      class="nt-form"
      novalidate="true"
    >
      <div v-if="!posted">
        <div
          v-show="!showCustomResult"
          class="nt-form__items"
        >
          <slot />
        </div>

        <div v-show="showCustomResult">
          <slot name="customresult" />
        </div>
      </div>

      <div
        v-else
        class="nt-form__success"
      >
        <div>
          {{ successMessage }}
          <div
            v-if="createsSampleIdentifier"
            class="nt-form__sucessIdentifier"
          >
            <c-text
              color="#233037"
              size="md"
              weight="400"
            >
              <span v-dompurify-html="sampleRequestMessage" />
            </c-text>
          </div>
          <div class="nt-form__sucessButtons">
            <slot name="form-buttons" />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import qs from 'qs'
import _ from 'lodash'
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'
import emitter from 'tiny-emitter/instance'
import { isCNPJ } from 'validation-br'
import utils from '../../../utils/utils'

export default {
  name: 'nt-form',
  props: {
    id: {
      type: String,
      default: 'form-contact'
    },
    errorMessages: {
      type: Object
    },
    urlAction: {
      type: String
    },
    successMessage: {
      type: String,
      default: null
    },
    customResult: {
      type: Boolean,
      default: false
    },
    decorated: {
      type: Boolean,
      default: false
    },
    createsSampleIdentifier: {
      type: Boolean,
      default: false
    },
    taggingFields: {
      type: Object,
      default: () => ({})
    },
    autocompleteFieldMapping: {
      type: String,
      default: '{}'
    }
  },
  data() {
    return {
      labelValues: {},
      key: Math.floor(Math.random() * 10000),
      posted: false,
      showCustomResult: false,
      sampleRequestMessage: '',
      gtmData: {}
    }
  },
  formDataStatic: {}, // keeps form data do convert to json on form submit. Use via this.$options.formDataStatic attribute.
  computed: {
    ...mapGetters('formStore', ['formData', 'validators']),
    ...mapState('formStore', ['errors']),
    messageAccepted() {
      return this.formData[this.taggingFields?.message_accepted] === '1' ? 'Sim' : 'Não'
    },
    parsedMapping() {
      try {
        return JSON.parse(this.autocompleteFieldMapping)
      } catch {
        return {}
      }
    },
    isSampleForm() {
      return this.createsSampleIdentifier || window.location.pathname.includes('relatorio-de-amostras')
    }
  },
  watch: {
    errors: {
      handler(values) {
        this.$nextTick(() => {
          this.focusInError(values)
        })
      },
      deep: true
    }
  },
  created() {
    this.onChangeForm({
      [this.id]: 'form'
    })

    emitter.on('showCustomResult', this.onShowCustomResult)
    emitter.on('changeForm', this.onChangeForm)
    emitter.on('storeRealValue', this.storeRealValue)
    emitter.on('formSubmit', this.onSubmit)
    emitter.on('selectedItem', (selectedData) => {
      Object.keys(this.taggingFields).forEach(key => {
        if (this.taggingFields[key] === selectedData.id) {
          this.gtmData[key] = selectedData.text
        }
      })
    })
    emitter.on('fetchViaCep', this.fetchViaCep)
  },
  methods: {
    ...mapActions('appStore', [
      'changeLoading'
    ]),
    ...mapActions('formStore', [
      'changeFormData',
      'addError',
      'clearErrors'
    ]),
    onChangeForm(val) {
      this.changeFormData(val)
      Object.assign(this.$options.formDataStatic, val)
    },
    onShowCustomResult(val) {
      this.showCustomResult = val

      if (!val) this.reload()

      this.changeScroll()
    },
    reload() {
      this.key = Math.floor(Math.random() * 10000)
    },
    validateProperty(property, validations) {
      const prop = this.formData[property]

      _.each(validations, (validation) => {
        switch (validation) {
          case 'email':
            this.checkEmail(prop, property, validation)
            break
          case 'required':
            this.checkRequired(prop, property, validation)
            break
          case 'cnpj':
            this.checkCnpj(prop, property, validation)
            break
          default:
            break
        }
      })
    },
    storeRealValue(val) {
      Object.assign(this.labelValues, val)
    },
    getLabelFromFormFields(values) {
      const fieldIdsWithErrors = _.uniq(values.map(error => Object.keys(error)[0]))

      const getLabelText = (id) => {
        const element = document.getElementById(id)

        if (element && element.tagName === 'A') {
          return element.textContent.trim()
        }

        const label = document.querySelector(`label[for="${id}"]`)

        return label ? label.textContent.trim() : id
      }

      const getLabelForTextarea = (id) => {
        const textarea = document.getElementById(id)
        if (textarea) {
          const label = textarea.getAttribute('label')
          return label ? label.trim() : id
        }
        return id
      }

      const getLabelForUl = (element, id) => {
        const label = element.previousElementSibling
        return label ? label.textContent.trim() : id
      }

      const fieldNamesWithErrors = fieldIdsWithErrors.map(id => {
        const element = document.getElementById(id)

        if (element && element.tagName === 'UL') {
          return getLabelForUl(element, id)
        }

        if (element && element.tagName === 'TEXTAREA') {
          return getLabelForTextarea(id)
        }

        return getLabelText(id)
      })

      const fieldQuantityWithErrors = fieldNamesWithErrors.length

      window.dataLayer.push({
        event: 'form_error',
        form_name: this.taggingFields?.form_name || '',
        field_name_error: fieldNamesWithErrors.join(', '),
        field_quantity_error: fieldQuantityWithErrors || 0
      })
    },
    pushGTM() {
      window.dataLayer.push({
        event: 'form_submit',
        form_name: this.taggingFields?.form_name || '',
        company_name: this.formData[this.taggingFields?.company_name] || '',
        message_accepted: this.messageAccepted,
        ...this.gtmData
      })
    },
    pushSampleGTM() {
      window.dataLayer.push({
        event: 'form_submit',
        form_name: this.taggingFields?.form_name || '',
        company_name: this.formData[this.taggingFields?.company_name] || '',
        name: this.formData[this.taggingFields?.name] || '',
        product: this.formData[this.taggingFields?.product] || '',
        quantity: this.formData[this.taggingFields?.quantity] || '',
        label: this.formData[this.taggingFields?.label] || '',
        category: this.formData[this.taggingFields?.category] || '',
        ...this.gtmData
      })
    },
    pushErrorGTM(error) {
      window.dataLayer.push({
        event: 'form_error',
        form_name: this.taggingFields?.form_name || '',
        field_name_error: error.field || '',
        field_quantity_error: this.taggingFields?.field_quantity_error || '',
        ...this.gtmData
      })
    },
    checkRequired(val, property, validation) {
      if (val) return

      this.addError({
        [property]: this.errorMessages[validation]
      })
    },
    checkEmail(val, property, validation) {
      if (val && utils.isValidEmail(val)) return

      this.addError({
        [property]: this.errorMessages[validation]
      })
    },
    checkCnpj(val, property, validation) {
      if (val && isCNPJ(val)) return

      this.addError({
        [property]: this.errorMessages[validation] || 'CNPJ inválido'
      })
    },
    isValidForm() {
      this.clearErrors()

      _.each(this.validators, (validations, property) => {
        this.validateProperty(property, validations)
      })
      return !this.errors.length
    },
    changePosted() {
      this.posted = !this.posted

      if (this.posted) {
        emitter.emit('posted')
        this.changeScroll()
        if (window.conversionCode && window.gtag) {
          window.gtag('event', 'conversion', { send_to: window.conversionCode })
        }
      }
    },
    changeScroll() {
      const { form } = this.$refs
      const position = form.getBoundingClientRect().top + window.scrollY - 206

      document.body.scrollTop = position // For Safari
      document.documentElement.scrollTop = position // For Chrome, Firefox, IE and Opera
    },
    focusInError(values) {
      if (!values) return

      const key = _.findKey(_.get(values, 0))
      const element = document.getElementById(key)

      if (!element) return

      const topPos = element.getBoundingClientRect().top + window.scrollY - 206

      document.body.scrollTop = topPos // For Safari
      document.documentElement.scrollTop = topPos // For Chrome, Firefox, IE and Opera
    },
    onSubmit() {
      if (!this.isValidForm()) {
        this.getLabelFromFormFields(this.errors)
        return
      }

      this.changeLoading(true)

      axios.post(this.urlAction, {
        formData: qs.stringify(this.$options.formDataStatic, {
          arrayFormat: 'brackets'
        })
      }).then((response) => {
        this.changePosted()
        this.sampleRequestMessage = response.data.sampleRequestMessage

        if (this.isSampleForm) {
          this.pushSampleGTM()
        } else {
          this.pushGTM()
        }
      }).catch((data) => {
        window.console.log(data)
        this.pushErrorGTM({ field: data?.response?.data?.field || '' })
      }).then(() => {
        this.changeLoading(false)
        this.reload()
      })
    },
    fetchViaCep: _.debounce(({ cep, mapping }) => {
      const parsedMapping = typeof mapping === 'string'
        ? JSON.parse(mapping)
        : mapping || {}

      axios.get(`https://viacep.com.br/ws/${cep}/json/`)
        .then(({ data }) => {
          if (!data.erro) {
            const mappedFields = {}

            Object.entries(parsedMapping).forEach(([apiField, inputId]) => {
              if (data[apiField]) {
                mappedFields[inputId] = data[apiField]
              }
            })

            emitter.emit('changeForm', mappedFields)
          }
        })
    }, 400)
  }
}
</script>

<style lang="scss" scoped>
.nt-form__items {
  display: flex;
  flex-direction: column;
}
</style>
